import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { hydraSearchToAppSearch } from "src/api/_common/hydra/search";
import { isQueryReady } from "src/api/axiosClient/utils";
import {
    IngredientScoreSourceMetaPayload,
    IngredientScoreSourceMetaPayloadSchema,
    OlapScoresResult,
    OlapScoresResultSchema,
} from "../olapScores/olapScoresSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";

type Options = {
    cacheTime?: number;
    search?: string;
    campaignId?: number;
    companyId?: number;
    scoresQuery?: OlapScoresQuery;
    params?: AxiosParams;
};

export const ingredientGridKey = "allIngredientsGrid" as const;

export const useGetAllIngredient = (options?: Options) => {
    const { cacheTime = 0, search = "", campaignId, companyId, scoresQuery, params = {} } = options || {};

    // This condition helps making less requests on Ingredients Page
    // But it also makes impossible the call to ingredients on other pages that
    // require a list of ingredient without scores (campaignId + companyId)
    // const isEnabled = campaignId !== undefined && companyId !== undefined;
    const isEnabled = true;

    const paramsKey = JSON.stringify(params); // Serialize params for proper cache key
    const query = useQuery({
        queryKey: [ingredientGridKey, search, campaignId, companyId, scoresQuery, paramsKey],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled: isEnabled,
        queryFn: async () => {
            const url = "/api/ingredients";

            const request = await loggedInApiCall.get(url, {
                params: {
                    ...params,
                    q: search !== "" ? search : undefined,
                    campaign: campaignId,
                    organization_id: companyId,
                    ...scoresQuery,
                },
            });
            const totalItems = request.data["hydra:totalItems"];
            const ingredient = request.data["hydra:member"];
            const appSearch = hydraSearchToAppSearch(request.data["hydra:search"]);
            const data = zodParseWithLog(ingredient, IngredientListSchema.array(), "allIngredients");
            return {
                ingredients: data,
                totalItems: totalItems,
                appSearch,
            };
        },
        onError: () => {
            return {
                ingredients: [],
                totalItems: 0,
                appSearch: {},
            };
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ...query.data,
        isReady,
        ...query,
    };
};

type Domain = {
    "@id": IRI;
    name: string;
};
const DomainSchema: z.ZodSchema<Domain> = z.object({
    "@id": z.string(),
    name: z.string(),
});

type IngredientLine = {
    "@id": IRI;
    id: number;
    name: string;
};
const IngredientLineZodSchema: z.ZodSchema<IngredientLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

type OwnedBy = {
    "@id": string;
    id: number;
};

const OwnedBySchema: z.ZodSchema<OwnedBy> = z.object({
    "@id": z.string(),
    id: z.number(),
});

export type IngredientList = {
    "@id": IRI;
    id: number;
    ingredientLine?: IngredientLine;
    ingredientSuppliersCount: number;
    name: string;
    externalId?: number;
    ingredientReferenceId?: number;
    referenceSource?: "sure" | "foodpilot";
    clientId?: string;
    organic?: boolean;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    olapScores: OlapScoresResult;
    /**
     * Per-supplier "where does this score come from?" sidecar emitted by
     * `OlapScoresNormalizer` for Ingredient rows. Optional because legacy /
     * non-Ingredient endpoints don't surface it. Used by the grid to overlay
     * a `ScoreSourceChip` on each score cell (cf. plan §FE-21).
     */
    scoreSourceMeta?: IngredientScoreSourceMetaPayload;
    ownedBy?: OwnedBy;
    tags?: Record<string, string[]>; // Format objet groupé par type de tag
    jsonAttributes?: Record<string, any>;
};

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const IngredientTagsSchema = z
    .union([
        z.record(z.string(), z.array(z.string())), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, string[]> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, string[]>>;

const IngredientListSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    clientId: z.string().optional(),
    domains: DomainSchema.array().optional(),
    externalId: z.number().optional(),
    ingredientSuppliersCount: z.number(),
    ingredientLine: IngredientLineZodSchema.optional(),
    ingredientReferenceId: z.number().optional(),
    referenceSource: z.union([z.literal("sure"), z.literal("foodpilot")]).optional(),
    name: z.string(),
    organic: z.boolean().optional(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    olapScores: OlapScoresResultSchema,
    scoreSourceMeta: IngredientScoreSourceMetaPayloadSchema.optional(),
    ownedBy: OwnedBySchema.optional(),
    tags: IngredientTagsSchema.optional(), // Accepte objet ou tableau vide
    jsonAttributes: z
        .union([z.record(z.string(), z.any()), z.array(z.any())])
        .optional()
        .transform((val) => {
            // Si c'est un tableau, le convertir en objet vide pour compatibilité
            if (Array.isArray(val)) {
                return {};
            }
            return val;
        }),
});
